import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Card, Row, Col, Table, Tag, Select, Button, Space, Statistic, Tabs, message, Timeline,
  Modal, Input, Popconfirm,
} from 'antd';
import { ApartmentOutlined, ReloadOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../utils/config';
import { enhanceColumns } from '../ortak/tableHelpers';
import './Finans.css';
import '../ortak/nfmodules.css';

const { Title } = Typography;
const hdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });
const unwrap = (r) => (r.data?.data !== undefined ? r.data.data : r.data);

const GOREV_DURUM = {
  beklemede: { label: 'Bekliyor', color: 'gold' },
  tamamlandi: { label: 'Tamamlandı', color: 'green' },
  iptal: { label: 'İptal', color: 'default' },
};
const TIP_RENK = {
  yer_sahibi_onay: 'volcano', belediye_onay: 'lime',
  insaat_talep: 'blue', insaat_kurulum: 'orange', enerji_surec: 'gold', hakedis_olustur: 'magenta',
  hakedis_onay: 'purple', hakedis_aktarim: 'geekblue', depo_hakedis: 'cyan',
};
const IZIN_TIPLERI = ['yer_sahibi_onay', 'belediye_onay'];
const fmtDT = (v) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '');

export default function SurecIzlemePage() {
  const navigate = useNavigate();
  const [ozet, setOzet] = useState(null);
  const [gorevler, setGorevler] = useState([]);
  const [akis, setAkis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fDurum, setFDurum] = useState('beklemede');
  const [fTip, setFTip] = useState(null);
  const [rejectRow, setRejectRow] = useState(null);
  const [sebep, setSebep] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fDurum) params.durum = fDurum;
      if (fTip) params.tip = fTip;
      const [o, g, a] = await Promise.all([
        axios.get(`${API_BASE_URL}/surec/ozet`, hdr()),
        axios.get(`${API_BASE_URL}/surec/gorevler`, { ...hdr(), params }),
        axios.get(`${API_BASE_URL}/surec/akis`, { ...hdr(), params: { limit: 200 } }),
      ]);
      setOzet(unwrap(o) || {});
      setGorevler(unwrap(g) || []);
      setAkis(unwrap(a) || []);
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Süreç verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [fDurum, fTip]);
  useEffect(() => { load(); }, [load]);

  const izinKarar = useCallback(async (row, k, s) => {
    try {
      await axios.post(`${API_BASE_URL}/surec/izin/${row.referans_id}/karar`,
        { karar: k, sebep: s || null }, hdr());
      message.success(k === 'onay' ? 'İzin onaylandı' : 'İzin reddedildi');
      setRejectRow(null); setSebep('');
      load();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'İşlem başarısız');
    }
  }, [load]);

  const sumWhere = (arr, pred) => (arr || []).filter(pred).reduce((s, r) => s + Number(r.n || 0), 0);
  const acikGorev = ozet?.acik_gorev || 0;
  const izinBekleyen = ozet?.izin_bekleyen || 0;
  const bekleyenHakedis = sumWhere(ozet?.hakedis, (r) => r.durum === 'onay_bekliyor');
  const bekleyenOdeme = sumWhere(ozet?.odeme_talebi, (r) => r.durum === 'Beklemede');
  const teslimBekleyen = sumWhere(ozet?.teslim_alma, (r) => String(r.durum) === '1');

  const gorevCols = [
    { title: 'Tür', dataIndex: 'tip_ad', width: 170, render: (v, r) => <Tag color={TIP_RENK[r.tip] || 'default'}>{v}</Tag> },
    { title: 'Başlık', dataIndex: 'baslik', ellipsis: true },
    {
      title: 'Sorumlu', dataIndex: 'sorumlu_ad', width: 180,
      render: (v, r) => v || (r.sorumlu_rol ? `Rol: ${r.sorumlu_rol}` : '-'),
    },
    {
      title: 'Durum', dataIndex: 'durum', width: 120,
      render: (v) => <Tag color={(GOREV_DURUM[v] || {}).color}>{(GOREV_DURUM[v] || {}).label || v}</Tag>,
    },
    { title: 'Oluşturan', dataIndex: 'olusturan_ad', width: 160, ellipsis: true },
    { title: 'Tarih', dataIndex: 'eklenmeTarihi', width: 140, render: fmtDT },
    {
      title: 'İşlem', key: 'a', width: 190, noFilter: true,
      render: (_, r) => {
        if (IZIN_TIPLERI.includes(r.tip) && r.durum === 'beklemede') {
          return (
            <Space size={4}>
              <Popconfirm title="İzni onaylıyor musunuz?" okText="Onayla" cancelText="Vazgeç"
                onConfirm={() => izinKarar(r, 'onay')}>
                <Button size="small" type="primary" icon={<CheckOutlined />}>Onayla</Button>
              </Popconfirm>
              <Button size="small" danger icon={<CloseOutlined />}
                onClick={() => { setRejectRow(r); setSebep(''); }}>Reddet</Button>
            </Space>
          );
        }
        return r.url ? <Button size="small" onClick={() => navigate(r.url)}>Git</Button> : null;
      },
    },
  ];

  const TIP_OPTS = [
    { value: 'yer_sahibi_onay', label: 'Yer Sahibi Onayı' },
    { value: 'belediye_onay', label: 'Belediye Onayı' },
    { value: 'insaat_talep', label: 'İnşaat: Planlama' },
    { value: 'insaat_kurulum', label: 'İnşaat: Kurulum' },
    { value: 'enerji_surec', label: 'Enerji Süreci' },
    { value: 'hakedis_olustur', label: 'Hakediş Oluşturma' },
    { value: 'hakedis_onay', label: 'Hakediş Onayı' },
    { value: 'hakedis_aktarim', label: 'Hakediş → Ödeme Talebi' },
    { value: 'depo_hakedis', label: 'Depo Teslim → Hakediş' },
  ];

  return (
    <div className="nf-module finans-page">
      <Title level={3} style={{ marginTop: 0 }}>
        <ApartmentOutlined style={{ marginRight: 8 }} /> Süreç İzleme
      </Title>

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={8}><Card size="small"><Statistic title="İzin Bekleyen (YS/Belediye)" value={izinBekleyen} valueStyle={{ color: '#d4380d' }} /></Card></Col>
        <Col xs={12} md={8}><Card size="small"><Statistic title="Açık Görev" value={acikGorev} valueStyle={{ color: '#d48806' }} /></Card></Col>
        <Col xs={12} md={8}><Card size="small"><Statistic title="Onay Bekleyen Hakediş" value={bekleyenHakedis} valueStyle={{ color: '#722ed1' }} /></Card></Col>
        <Col xs={12} md={8}><Card size="small"><Statistic title="Bekleyen Ödeme Talebi" value={bekleyenOdeme} valueStyle={{ color: '#1677ff' }} /></Card></Col>
        <Col xs={12} md={8}><Card size="small"><Statistic title="Hakediş Bekleyen Teslim" value={teslimBekleyen} valueStyle={{ color: '#13c2c2' }} /></Card></Col>
      </Row>

      <Space wrap style={{ marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={load}>Yenile</Button>
        <Select allowClear placeholder="Durum" style={{ width: 150 }} value={fDurum} onChange={setFDurum}
          options={[{ value: 'beklemede', label: 'Bekliyor' }, { value: 'tamamlandi', label: 'Tamamlandı' }, { value: 'iptal', label: 'İptal' }]} />
        <Select allowClear placeholder="Tür" style={{ width: 220 }} value={fTip} onChange={setFTip} options={TIP_OPTS} />
        <span style={{ color: '#888' }}>{gorevler.length} görev</span>
      </Space>

      <Tabs
        defaultActiveKey="gorevler"
        items={[
          {
            key: 'gorevler', label: 'Görevler',
            children: (
              <Table rowKey="id" size="small" loading={loading} dataSource={gorevler}
                columns={enhanceColumns(gorevCols)} scroll={{ x: 'max-content', y: 460 }}
                pagination={{ pageSize: 50 }} />
            ),
          },
          {
            key: 'akis', label: 'Süreç Akışı (Son Hareketler)',
            children: (
              <Card size="small" loading={loading} bodyStyle={{ maxHeight: 520, overflow: 'auto' }}>
                <Timeline
                  items={(akis || []).map((l) => ({
                    color: (l.islem === 'red' || l.islem === 'izin_red') ? 'red'
                      : ((l.islem === 'onaylandi' || l.islem === 'odendi' || l.islem === 'izin_onay') ? 'green' : 'blue'),
                    children: (
                      <div>
                        <b>{l.referans_tablo} #{l.referans_id}</b> — {l.islem}
                        {l.onceki_durum || l.yeni_durum ? <span style={{ color: '#888' }}> ({l.onceki_durum || '-'} → {l.yeni_durum || '-'})</span> : null}
                        <div style={{ fontSize: 12, color: '#666' }}>
                          {fmtDT(l.tarih)}{l.yapan_ad ? ` · ${l.yapan_ad}` : ''}{l.aciklama ? ` · ${l.aciklama}` : ''}
                        </div>
                      </div>
                    ),
                  }))}
                />
                {(!akis || akis.length === 0) && <div style={{ color: '#888' }}>Kayıt yok.</div>}
              </Card>
            ),
          },
        ]}
      />

      <Modal title="İzin Reddet" open={!!rejectRow} okText="Reddet" cancelText="Vazgeç"
        okButtonProps={{ danger: true }} onOk={() => izinKarar(rejectRow, 'red', sebep)}
        onCancel={() => { setRejectRow(null); setSebep(''); }} destroyOnClose>
        {rejectRow && <div style={{ marginBottom: 8, color: '#888' }}>{rejectRow.baslik}</div>}
        <Input.TextArea rows={3} placeholder="Red gerekçesi (zorunlu)"
          value={sebep} onChange={(e) => setSebep(e.target.value)} />
      </Modal>
    </div>
  );
}
